/**
 * インベントリの「現在の表示状態」を一元管理する純粋ストア（自前・依存ゼロ）。
 *
 * - サーバーからの InventoryState 配信を受け取って保持する。
 * - 装備品 effects は派生プロパティ（最新装備からの計算）として `effectiveCueEffects` 等で取得。
 * - 管理人（isAdmin）の場合、表示上の保有を **全アイテム** に拡張する（サーバーの owned は変更しない）。
 */

import type { InventoryItem, ItemCategory, ItemEffects, ItemId, InventoryState } from './types.js';
import { ALL_ITEM_IDS, ITEM_REGISTRY, getItem } from './registry.js';

/** ローカル ストア（main から 1 つだけ生成）。 */
export interface InventoryStore {
  /** 現在表示するべき保有・装備状態（管理人なら ALL_ITEM_IDS が owned に展開される）。 */
  current: () => InventoryState;
  /** 管理人フラグの設定（サーバーからの statsUpdate に同梱）。 */
  setAdmin: (isAdmin: boolean) => void;
  /** 現在の管理人フラグ値（UI のバッジ表示に使用）。 */
  isAdmin: () => boolean;
  /** サーバーからの inventory 配信を反映。 */
  apply: (state: InventoryState) => void;
  /** 装備カテゴリで現在装備中のアイテム定義（未装備は null）。 */
  equippedItem: (cat: ItemCategory) => InventoryItem | null;
  /** 装備中アイテムの effects を集約（同カテゴリ1個・現状 cue のみ実利あり）。 */
  effectiveCueEffects: () => ItemEffects;
  /** 装備中 skill カテゴリの effects（自動照準・軌跡ガイド）。未装備なら空。 */
  effectiveSkillEffects: () => ItemEffects;
  /** 保有する全アイテムの autoAimMatchMaxCutDeg の最大値（0=該当なし）。
   *  ドリル内オートエイム（保有範囲で有効・案A）の許容カット角に使う。装備に依らず owned ベース。
   *  管理人は ALL_ITEM_IDS が owned 扱いになるため全スキルの最大が反映される。 */
  maxOwnedAutoAimCutDeg: () => number;
  /** 変更通知の購読（current が変わるたびに呼ばれる）。 */
  subscribe: (cb: () => void) => void;
}

export const createInventoryStore = (): InventoryStore => {
  let raw: InventoryState = { owned: [], equipped: {} };
  let isAdmin = false;
  const listeners: Array<() => void> = [];

  const expanded = (): InventoryState => {
    if (!isAdmin) return raw;
    // 管理人は表示上全アイテム所持。装備は raw.equipped をそのまま使用（未装備なら自分で装備して切替可能）。
    return { owned: ALL_ITEM_IDS, equipped: raw.equipped };
  };

  const notify = (): void => {
    for (const cb of listeners) {
      try {
        cb();
      } catch (err) {
        console.error('inventory store subscribe コールバック失敗:', err);
      }
    }
  };

  return {
    current: expanded,
    setAdmin: (v: boolean): void => {
      if (isAdmin === v) return;
      isAdmin = v;
      notify();
    },
    isAdmin: (): boolean => isAdmin,
    apply: (state: InventoryState): void => {
      raw = {
        owned: Array.isArray(state.owned) ? state.owned.slice() : [],
        equipped: state.equipped ? { ...state.equipped } : {},
      };
      notify();
    },
    equippedItem: (cat: ItemCategory): InventoryItem | null => {
      const id = expanded().equipped[cat];
      return id ? getItem(id) : null;
    },
    effectiveCueEffects: (): ItemEffects => {
      const cue = expanded().equipped.cue;
      if (!cue) return {};
      const item = getItem(cue);
      return item?.effects ?? {};
    },
    effectiveSkillEffects: (): ItemEffects => {
      const skill = expanded().equipped.skill;
      if (!skill) return {};
      const item = getItem(skill);
      return item?.effects ?? {};
    },
    maxOwnedAutoAimCutDeg: (): number => {
      let max = 0;
      for (const id of expanded().owned) {
        const deg = getItem(id)?.effects.autoAimMatchMaxCutDeg;
        if (typeof deg === 'number' && deg > max) max = deg;
      }
      return max;
    },
    subscribe: (cb: () => void): void => {
      listeners.push(cb);
    },
  };
};

/** registry 露出（呼び出し側で名前/説明/効果を取り出すヘルパ）。 */
export const getItemDefinition = (id: ItemId): InventoryItem | null => getItem(id);
export const allItemDefinitions = (): readonly InventoryItem[] => Object.values(ITEM_REGISTRY);
